/**
 * Auth API — login Google, fetch profile, logout (plan 16 MVP, Task A6).
 *
 * Session token + account disimpan via lib/auth.ts setelah login sukses.
 * Logout selalu clear local session walaupun request server gagal
 * (token yang sudah tidak terkirim = sudah logout dari sudut pandang user).
 */

import { apiFetch } from './client';
import {
  clearAuthSession,
  saveAuthSession,
  getAuthSession,
  type AuthAccount,
} from '../auth';
import { getOrCreateDeviceId } from '../device-id';
import { getOrCreateGuestSecret } from '../guest-secret';

interface GoogleSignInResponse {
  session_token: string;
  account: AuthAccount;
  signup_bonus?: {
    granted: boolean;
    amount: number;
  };
}

export interface GoogleSignInResult {
  account: AuthAccount;
  signupBonus: { amount: number } | null;
}

interface MeResponse {
  account: AuthAccount;
  /** Plan 17 UX fix: endpoint /me ikut melaporkan trial + link device. */
  trial_ends_at?: string;
  device_linked?: boolean;
}

interface GuestSignInResponse {
  session_token: string;
  account: AuthAccount & { is_guest: true };
  trial_ends_at: string;
  /** Server-side: browser ini pernah login akun asli (tabel device_link). */
  device_linked?: boolean;
}

/** Hasil bootstrap guest — dipakai entry screen untuk deteksi user lama. */
export interface GuestBootstrapResult {
  account: AuthAccount;
  /** ISO timestamp akhir trial; null bila sesi sudah ada sehingga tidak ada fetch. */
  trialEndsAt: string | null;
  /** true bila device ini pernah login akun asli (sinyal retroaktif dari server). */
  deviceLinked: boolean;
}

/** Hasil GET /v1/auth/me — untuk sesi guest yang sudah menggantung. */
export interface MeInfo {
  account: AuthAccount;
  trialEndsAt: string | null;
  deviceLinked: boolean;
}

let guestBootstrapPromise: Promise<GuestBootstrapResult> | null = null;

/**
 * Tukar Google ID token (dari GIS button) dengan session token server.
 * Menyimpan sesi secara lokal; return account profile.
 */
export async function signInWithGoogle(idToken: string): Promise<GoogleSignInResult> {
  const res = await apiFetch<GoogleSignInResponse>('/v1/auth/google', {
    method: 'POST',
    body: {
      id_token: idToken,
      device_id: getOrCreateDeviceId(),
      guest_secret: getOrCreateGuestSecret(),
    },
  });
  saveAuthSession({ token: res.session_token, account: res.account });
  return {
    account: res.account,
    signupBonus: res.signup_bonus?.granted ? { amount: res.signup_bonus.amount } : null,
  };
}

/** Ambil ulang profile account dari server (validasi sesi masih hidup). */
export async function fetchMe(): Promise<MeInfo> {
  const res = await apiFetch<MeResponse>('/v1/auth/me');
  return {
    account: res.account,
    trialEndsAt: res.trial_ends_at ?? null,
    deviceLinked: res.device_linked === true,
  };
}

/**
 * Plan 17: pastikan ada sesi aktif. Kalau belum ada (first launch),
 * register guest account dengan device_id dan simpan sesinya.
 * Idempotent — aman dipanggil berulang. Device yang pernah login akun
 * asli dilaporkan lewat deviceLinked (sinyal retroaktif dari server),
 * dan trial_ends_at dihitung dari created_at guest asli → entry screen
 * bisa mengantar user lama / trial kedaluwarsa langsung ke /login.
 */
export function ensureGuestSession(): Promise<GuestBootstrapResult> {
  const existing = getAuthSession();
  if (existing) {
    return Promise.resolve({ account: existing.account, trialEndsAt: null, deviceLinked: false });
  }
  if (!guestBootstrapPromise) {
    guestBootstrapPromise = (async () => {
      const res = await apiFetch<GuestSignInResponse>('/v1/auth/guest', {
        method: 'POST',
        body: {
          device_id: getOrCreateDeviceId(),
          guest_secret: getOrCreateGuestSecret(),
        },
      });
      saveAuthSession({ token: res.session_token, account: res.account });
      return {
        account: res.account,
        trialEndsAt: res.trial_ends_at ?? null,
        deviceLinked: res.device_linked === true,
      };
    })().finally(() => {
      guestBootstrapPromise = null;
    });
  }
  return guestBootstrapPromise;
}

/** Revoke sesi di server + clear local. Tidak pernah throw. */
export async function logout(): Promise<void> {
  try {
    await apiFetch('/v1/auth/logout', { method: 'POST' });
  } catch {
    // Network error / sesi sudah revoked — tetap lanjut clear lokal.
  } finally {
    clearAuthSession();
  }
}
